<script setup lang="ts">
import { ref, computed } from 'vue';
import { type Duration, format } from "date-fns";

const props = defineProps<{
  title: string,
  isInsights?: boolean,
  description: string,
  elements: {
    title?: string,
    description?: string,
    icon?: string,
    image?: string,
    imageFullUrl?: string,
    href?: string,
    numberedTitle?: { prefix?: string, number?: number, date?: Date, time?: Duration, postfix?: string }
  }[],
  columns: number,
  animate?: boolean
}>();

const imageError = ref<boolean[]>(props.elements.map(() => false));

const handleImageError = (index: number) => {
  imageError.value[index] = true;
};

const hasValidImages = computed(() => {
  return !(props.elements.some(e => e.image)) || props.elements.some((element, index) => element.image && !imageError.value[index]);
});

const { open: openLightbox } = useLightbox();

const photoUrls = computed(() =>
  props.elements.filter(e => e.imageFullUrl).map(e => e.imageFullUrl as string)
);

function openPhoto(url?: string) {
  if (!url) return;
  const urls = photoUrls.value;
  openLightbox(urls, Math.max(0, urls.indexOf(url)));
}
</script>

<template>
  <section class="text-center" v-if="hasValidImages">
    <div class="flex items-center justify-center gap-2">
      <span
        v-if="isInsights"
        class="inline-flex items-center rounded-md bg-stone-900 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
        {{ $t('aiAbbreviation') }}
      </span>
      <h2 class="text-xl font-extrabold text-stone-900">{{ title }}</h2>
    </div>
    <p class="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-stone-500" v-html="description"></p>

    <div :class="`grid grid-cols-${columns} gap-3 mt-6`">
      <template v-for="(element, index) in elements" :key="index">

        <!-- Photo card -> opens lightbox -->
        <button
          v-if="element.image && element.imageFullUrl && !imageError[index]"
          type="button"
          :data-aos="animate ? 'fade-up' : false"
          class="group relative overflow-hidden rounded-2xl border border-stone-200 bg-stone-100 shadow-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-explorial-500"
          @click="openPhoto(element.imageFullUrl)">
          <img
            :src="element.image"
            alt=""
            class="aspect-square w-full object-cover transition duration-300 group-hover:scale-[1.04]"
            @error="() => handleImageError(index)" />
          <span class="pointer-events-none absolute inset-0 bg-stone-950/0 transition group-hover:bg-stone-950/10"></span>
        </button>

        <!-- Linked image card (e.g. nearby trails) -->
        <a
          v-else-if="element.image && element.href && !imageError[index]"
          :href="element.href"
          target="_blank"
          rel="noopener noreferrer"
          :data-aos="animate ? 'fade-up' : false"
          class="group block overflow-hidden rounded-2xl border border-stone-200 bg-white text-left shadow-soft transition hover:shadow-soft-lg">
          <div class="overflow-hidden">
            <img
              :src="element.image"
              alt=""
              class="aspect-[4/3] w-full object-cover transition duration-300 group-hover:scale-[1.04]"
              @error="() => handleImageError(index)" />
          </div>
          <div class="p-3">
            <h3 v-if="element.title" class="font-bold text-stone-900">{{ element.title }}</h3>
            <p v-if="element.description" class="text-sm text-stone-500">{{ element.description }}</p>
          </div>
        </a>

        <!-- Skip cards whose only content was an image that failed to load -->
        <template v-else-if="element.image && !element.href" />

        <!-- Text / stat / numbered card -->
        <div
          v-else
          :data-aos="animate ? 'flip-down' : false"
          :class="[
            'rounded-2xl border border-stone-200 bg-white p-5 shadow-soft',
            columns == 1 ? 'text-left' : 'text-center'
          ]">
          <UIcon
            v-if="element.icon"
            :name="element.icon"
            dynamic
            :class="[
              'h-7 w-7',
              columns == 1 ? 'float-right ml-3 text-stone-400' : 'mx-auto mb-2 block text-explorial-500'
            ]" />

          <h3 v-if="element.title" class="font-bold text-stone-900">{{ element.title }}</h3>

          <h3 v-if="element.numberedTitle" class="text-2xl font-extrabold tracking-tight text-stone-900">
            {{ element.numberedTitle.prefix }}
            <AnimatedNumber v-if="element.numberedTitle.number" :endVal="element.numberedTitle.number" :duration="2"/>
            <span v-if="element.numberedTitle.date"><AnimatedNumber :endVal="parseInt(format(element.numberedTitle.date, 'dd'))" :duration="2" :options="{leadingZero: true}"/>.<AnimatedNumber
                :endVal="parseInt(format(element.numberedTitle.date, 'MM'))" :duration="2" :options="{leadingZero: true}"/>.<AnimatedNumber
                :endVal="parseInt(format(element.numberedTitle.date, 'yyyy'))" :duration="2" :options="{leadingZero: true}" separator=""/>
            </span><span v-if="element.numberedTitle.time"><AnimatedNumber :endVal="(element.numberedTitle.time.days || 0) * 24 + (element.numberedTitle.time.hours || 0)"
                                                                           :duration="2" :options="{leadingZero: false}"/>:<AnimatedNumber
              :endVal="element.numberedTitle.time.minutes || 0" :duration="2" :options="{leadingZero: true}"/>
            </span>{{ element.numberedTitle.postfix }}
          </h3>

          <p v-if="element.description" class="mt-1 text-sm leading-relaxed text-stone-500">{{ element.description }}</p>
        </div>
      </template>
    </div>
    <slot></slot>
  </section>
</template>

<style scoped></style>
